import { forwardRef, useState } from "react";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import { useRecoilValue } from "recoil";
import styled from "styled-components";

import { FilterPillBar } from "../FilterPillBar";
import { HorizontalPlaylistLayout } from "../HorizontalPlaylistLayout";
import { LeaguePlayerInfo } from "../LeaguePlayerInfo";
import { Thumbnails } from "../Thumbnails";
import { OPGGEmbeddablePlaylistViewModel, TagItem } from "../../ViewModel";

import { ESupportedGames } from "MVVM/@types";
import { LoadingSkeleton } from "MVVM/Components/Skeloton";
import {
  PlaylistClipsState,
  ActivePlaylistClipState,
  PlaylistLayoutState,
} from "MVVM/State/embeddablePlaylist";
import EmbeddablePlayer from "MVVM/Views/EmbeddablePlayer";

export const OPGGEmbeddablePlaylist = forwardRef<
  HTMLDivElement,
  {
    platform: string;
    handleMouseEnter: () => void;
  }
>(({ platform, handleMouseEnter }, ref) => {
  const playlistClips = useRecoilValue(PlaylistClipsState);
  const activePlaylistClip = useRecoilValue(ActivePlaylistClipState);
  const playlistLayout = useRecoilValue(PlaylistLayoutState);
  const [selectedTag, setSelectedTag] = useState<string>("all");

  const { filters, getClipsForTag } = OPGGEmbeddablePlaylistViewModel({
    platform,
  });

  if (playlistLayout === "horizontal")
    return <HorizontalPlaylistLayout platform={platform} />;

  const filterPills = filters.map((tag: TagItem) => ({
    label: tag.value,
    key: `${tag.key}:${tag.value}`,
    count: tag.clips.length,
  }));

  const allFilters = [
    { label: "All", key: "all", count: playlistClips?.length || 0 },
    ...filterPills,
    { label: "More", key: "more", count: 0, externalLink: true },
  ];

  const clipsToDisplay = getClipsForTag(selectedTag);

  return (
    <StyledContainer onMouseEnter={handleMouseEnter}>
      <OPGGHeader
        title={activePlaylistClip?.title || ""}
        isHorizontalLayout={false}
      />
      <StyledLeageWrapper>
        <FixedLeagueWrapper ref={ref} className="fixed-width-container">
          {activePlaylistClip ? (
            <EmbeddablePlayer
              className="player-component"
              userRole="viewer"
              onPlayEndVariant="nextUpPlaylist"
            />
          ) : (
            <PlayerLoadingSkeleton>
              <LoadingSkeleton width="100%" height="100%" />
            </PlayerLoadingSkeleton>
          )}
        </FixedLeagueWrapper>
        <LeaguePlayerInfoWrapper>
          <LeaguePlayerInfo isOPGG />
        </LeaguePlayerInfoWrapper>
      </StyledLeageWrapper>
      <StyledFilterContainer>
        {allFilters.length > 0 ? (
          <FilterPillBar
            filters={allFilters}
            selected={selectedTag}
            onSelect={setSelectedTag}
            game={ESupportedGames.LOL}
          />
        ) : (
          <FilterPillBarSkeleton>
            <Flex style={{ gap: "8px" }}>
              {Array.from({ length: 5 }, (_, i) => (
                <LoadingSkeleton
                  key={i}
                  width={48}
                  height={22}
                  style={{ borderRadius: "20px" }}
                />
              ))}
            </Flex>
          </FilterPillBarSkeleton>
        )}
      </StyledFilterContainer>
      <ThumbnailsWithPadding
        clipsToDisplay={clipsToDisplay}
        platform={platform}
        renderLolPlaylist
      />
    </StyledContainer>
  );
});

const StyledFilterContainer = styled(Flex)`
  display: none;
  padding-left: 10px;

  @media (max-width: 350px) {
    display: flex;
  }
`;

const FilterPillBarSkeleton = styled(Box)``;

const StyledBrandingContainer = styled(Flex)`
  align-items: center;
  gap: 4px;

  @media (max-width: 350px) {
    display: none;
  }
`;

const ThumbnailsWithPadding = styled(Thumbnails)`
  padding: 0 0 10px 10px;
`;

const StyledLeageWrapper = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 12px;
  min-height: 243px;
  width: 100%;

  padding-left: 10px;

  @media (max-width: 350px) {
    padding-right: 10px;
    min-height: 175px;
  }
`;

const FixedLeagueWrapper = styled(Box)`
  flex-shrink: 0;
  height: 100%;
  position: relative;
  width: 432px;
  z-index: 1;

  &:not(:has(:fullscreen)) {
    .vjs-tech {
      border-radius: 8px;
      height: auto !important;
      width: 100% !important;
    }
    .video-js {
      background: transparent;
      width: 100% !important;
      height: 100% !important;
      min-height: 243px;
    }
  }

  @media (max-width: 350px) {
    width: 100%;

    &:not(:has(:fullscreen)) {
      .video-js {
        min-height: 175px;
      }
    }
  }

  .player-component {
    position: relative;
  }
`;

const LeaguePlayerInfoWrapper = styled(Box)`
  align-items: center;
  display: flex;
  flex: 1 1 0%;
  height: 100%;
  justify-content: center;

  @media (max-width: 350px) {
    display: none;
  }
`;

const PlayerLoadingSkeleton = styled(Box)`
  border-radius: 8px;
  height: 100%;
  width: 100%;
`;

const StyledContainer = styled(Flex)`
  background: #282830;
  border-radius: 6px;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  height: 100%;
`;

export const OPGGHeader = ({
  title,
  isHorizontalLayout,
}: {
  title: string;
  isHorizontalLayout: boolean;
}) => (
  <StyledHeaderContainer $isHorizontalLayout={isHorizontalLayout}>
    <Flex style={{ gap: "5px" }}>
      <Text variant="text.paragraphSmall" fontWeight={700}>
        {title}
      </Text>
    </Flex>
    <StyledBrandingContainer>
      <StyledPoweredByText variant="text.labelXXSmall">
        Powered by
      </StyledPoweredByText>
      <Image
        width={48}
        height={10}
        src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/iframe/allstargg.png`}
        onClick={() => {
          const ajs_aid = window.rudderanalytics.getAnonymousId();
          const utmSource =
            new URLSearchParams(window.location.search).get("platform") ||
            "unknown";
          window.open(
            `https://allstar.gg?ajs_aid=${ajs_aid}&utm_source=${utmSource}`,
            "_blank",
          );
        }}
      />
    </StyledBrandingContainer>
  </StyledHeaderContainer>
);

const StyledPoweredByText = styled(Text)`
  color: #cddcfe;
`;

const StyledHeaderContainer = styled(Flex)<{ $isHorizontalLayout: boolean }>`
  align-items: center;
  justify-content: space-between;

  background-color: #31313c;
  padding: ${({ $isHorizontalLayout }) =>
    $isHorizontalLayout ? "10px 20px" : "15px 20px"};
  border-bottom: #1c1c1f 1px solid;

  @media (max-width: 350px) {
    padding: 10px;
  }
`;
